import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import classes from "../../../data-collection/data-quality-assessment/dqa.module.scss";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Close";
import Breadcrumbs from "../../../data-collection/desk-review/responses/Breadcrumbs";
import { dqaService } from "../../../../../services/dqaService";
import Dropdown from "../../../../controls/Dropdown";
import {
  DeskLevelSummaryReportModel,
  SummaryDataModel,
  ConsistentTrendValue,
  ConsistentTrendHelper,
} from "../../../../../models/DQA/DeskLevelSummaryReportModel";
import MultiSelectModel from "../../../../../models/MultiSelectModel";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Step, StepLabel, Stepper, Typography } from "@mui/material";
import RadioButtonGroup from "../../../../controls/RadioButtonGroup";
import TextBox from "../../../../controls/TextBox";
import { useSelector } from "react-redux";
import Table from "../../../data-collection/desk-review/responses/Table";
import TableBody from "../../../data-collection/desk-review/responses/TableBody";
import TableRow from "../../../data-collection/desk-review/responses/TableRow";
import TableCell from "../../../data-collection/desk-review/responses/TableCell";
import TableHeader from "../../../data-collection/desk-review/responses/TableHeader";
import NationalLevelSummaryValidationRules from "./NationalLevelSummaryValidationRules";
import useFormValidation from "../../../../common/useFormValidation";
import { UserAssessmentPermission } from "../../../../../models/PermissionModel";
import UserMessage from "../../../../common/UserMessage";
import { DQADLSummaryResultType } from "../../../../../models/Enums";
import InfoIcon from "@mui/icons-material/Info";
import { UtilityHelper } from "../../../../../utils/UtilityHelper";
import parse from "html-react-parser";
import Tooltip from "../../../../controls/Tooltip";

//Data analysis DL DQA National Level Summary report component which renders Result and Targets for core indicators

const MAX_DATA_QUALITY_REASON_LENGTH = 5000;

const DeskLevelSummaryComponent = () => {
  const { t } = useTranslation();
  document.title = t("app.DQASummary");
  const location: any = useLocation();
  const navigate = useNavigate();
  const assessmentId = location?.state?.assessmentId;
  const subTabIndex = location?.state?.subTabIndex;
  const [years, setYears] = useState<Array<string>>([]);
  const [summaryYear, setSummaryYear] = useState<string>("");
  const [serviceLevelData, setServiceLevelData] = useState<SummaryDataModel>(
    SummaryDataModel.init(assessmentId)
  );
  const [summaryData, setSummaryData] = useState<SummaryDataModel>(
    SummaryDataModel.init(assessmentId)
  );
  const userPermission: UserAssessmentPermission = useSelector(
    (state: any) => state.userPermission.assessment
  );
  const {
    canEditAndSaveDeskLevelDQASummaryResult,
    canFinalizeDeskLevelDQASummaryResult,
    canExportDeskLevelDQASummary,
  } = userPermission;
  const validate = useFormValidation(NationalLevelSummaryValidationRules);
  const errors = useSelector((state: any) => state.error);
  const [isDeskLevelSummaryDataSaved, setIsDeskLevelSummaryDataSaved] =
    useState<boolean>(false);
  const [isConcordanceApplicable, setIsConcordanceApplicable] =
    useState<boolean | null>(null);
  const [dataQualityReasonError, setDataQualityReasonError] = useState<string>("");
  // Tracks when a paste was truncated, so the change event that follows keeps the paste message
  const reasonPasteTruncatedRef = useRef<boolean>(false);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
  const [currentExportSteps, setCurrentExportSteps] = useState<string[]>([]);

  const exportSteps = [
    `${t("Common.Exporting")}...`,
    t("Common.ExportStepFetchingData"),
    t("Common.ExportStepGeneratingFile"),
  ];
  const handleProgress = (step: string) => {
    const idx = exportSteps.indexOf(step);
    setActiveStepIndex(idx >= 0 ? idx : 0);
  };
  const handleAllComplete = () => setActiveStepIndex(exportSteps.length);
  const resetExportProgress = () => setActiveStepIndex(-1);

  //Triggers whenever user takes an action on close or back navigation click
  const onBackOrCloseButtonClick = () => {
    const state = location?.state;

    navigate("/assessment/data-analysis/dqa", {
      state: {
        ...state,
        subTabIndex,
      },
    });
  };

  // Table headers
  const tableHeadersDataQuality = [
    t(
      "Assessment.DataCollection.DataQualityAssessment.NationalDataQualityEstimates"
    ),
    t("Assessment.DataCollection.DataQualityAssessment.NationalLevelResults"),
    t("Assessment.DataCollection.DataQualityAssessment.NationalLevelTarget"),
  ];

  useEffect(() => {
    bindSummaryYears();
    dqaService.getDeskLevelDQA(assessmentId)
      .then(response => {
        setIsConcordanceApplicable(response?.dataSystemsTwoId != null);
      })
      .catch(() => {
        // Default to applicable on error — safer than hiding the concordance field
        setIsConcordanceApplicable(true);
      });
  }, []);

  // Debug: Log when consistency over time changes
  useEffect(() => {
    console.log(
      "Burden Reduction - Consistency over time changed:",
      summaryData.consistencyOverTimeCoreIndicators
    );
  }, [summaryData.consistencyOverTimeCoreIndicators]);

  // Calculate consistency over time for core indicators based on current summary data
  const calculateConsistencyOverTime = (
    summaryData: DeskLevelSummaryReportModel
  ) => {
    const coreIndicatorFields = [
      "malariaOutpatientProportion",
      "malariaInPatientProportion",
      "malariaInPatientDeathProportion",
      "testPositivityRate",
      "slidePositivityRate",
      "rdtPositivityRate",
      "suspectedTestProportion",
    ];

    const nonNAIndicators = coreIndicatorFields.filter(
      field => summaryData[field] !== null
    );

    return (
      nonNAIndicators.length > 0 &&
      nonNAIndicators.every(field => summaryData[field] === true)
    );
  };

  // Triggers whenever a change event if fired from radio button
  const nationalLevelTargetRadioChange = (
    fieldName: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    console.log(
      `Burden Reduction - Radio change: ${fieldName} = ${e.target.value}`
    );

    const newValue = ConsistentTrendHelper.stringToBoolean(e.target.value);

    const _summaryData = {
      ...summaryData,
      [fieldName]: newValue,
    };

    const newConsistency = calculateConsistencyOverTime(_summaryData);
    _summaryData.consistencyOverTimeCoreIndicators = newConsistency;
    console.log(`Burden Reduction - New consistency value: ${newConsistency}`);

    setSummaryData(_summaryData);
  };

  //Helper function to get the default value for radio buttons
  const bindConsistencyOverTimeForCoreIndicators = (
    value: boolean | null,
    fieldName: string
  ) => {
    return ConsistentTrendHelper.fromBoolean(value);
  };

  // Triggers whenever the user modifies the national level target
  const nationalLevelTargetHandleChange = (
    fieldName: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const _summaryData = {
      ...summaryData,
      [fieldName]: e.currentTarget.value ? +e.currentTarget.value : null,
    };

    setSummaryData(_summaryData);
  };

  // Triggers whenever the user modifies the result reason change event
  const dataQualityResultReasonChange = (
    fieldName: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.currentTarget.value;
    const _summaryData = {
      ...summaryData,
      [fieldName]: value,
    };

    setSummaryData(_summaryData);

    if (
      reasonPasteTruncatedRef.current &&
      value.length >= MAX_DATA_QUALITY_REASON_LENGTH
    ) {
      // A paste was truncated to fit the limit; keep the paste-specific message
      setDataQualityReasonError(
        t(
          "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.reasonPasteTruncatedError"
        )
      );
    } else {
      // Show an inline error once the maximum character limit is reached
      setDataQualityReasonError(
        value.length >= MAX_DATA_QUALITY_REASON_LENGTH
          ? t(
              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.reasonMaxLengthError"
            )
          : ""
      );
    }
    reasonPasteTruncatedRef.current = false;
  };

  // Detects when pasted content would exceed the limit (and therefore gets truncated)
  const onDataQualityReasonPaste = (
    evt: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const input = evt.target as HTMLTextAreaElement;
    const pastedText = evt.clipboardData.getData("text");
    const currentValue = input.value ?? "";
    const selectionLength =
      (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
    const resultingLength =
      currentValue.length - selectionLength + pastedText.length;

    if (resultingLength > MAX_DATA_QUALITY_REASON_LENGTH) {
      reasonPasteTruncatedRef.current = true;
      setDataQualityReasonError(
        t(
          "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.reasonPasteTruncatedError"
        )
      );
    }
  };

  // Triggers whenever user tries to change dropdown the value
  const onChangeYears = (
    year: string,
    reportCompleteness: string,
    reportTimeliness: string,
    variableCompleteness: string,
    variableConsistency: string,
    variableConcordance: string,
    dataQualityResultReason: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.currentTarget.value != "") {
      // Validate the selected year
      const selectedYear = e.currentTarget.value;
      const parsedYear = parseInt(selectedYear);

      if (isNaN(parsedYear) || parsedYear <= 1900 || parsedYear > 2100) {
        console.error("Invalid year selected:", selectedYear);
        alert(`Invalid year selected: ${selectedYear}. Please select a valid year.`);
        return;
      }

      const _summaryData = {
        ...summaryData,
        [reportCompleteness]: "",
        [reportTimeliness]: "",
        [variableCompleteness]: "",
        [variableConsistency]: "",
        [variableConcordance]: "",
        [dataQualityResultReason]: "",
        [year]: selectedYear,
      };

      setSummaryData(_summaryData);
      setSummaryYear(selectedYear);

      bindNationalDataQualityEstimates(selectedYear);
    }
  };

  // Call Desk Level-DQA indicators API service and map to GetIndicatorModel.
  const bindNationalDataQualityEstimates = (summaryYear: string) => {
    dqaService
      .getDeskLevelSummaryResult(assessmentId, summaryYear)
      .then(response => {
        setServiceLevelData(response[0]);
        if (response.length > 1) {
          // Calculate the consistency over time based on current data
          response[1].consistencyOverTimeCoreIndicators =
            calculateConsistencyOverTime(response[1]);
          setSummaryData(response[1]);
          setIsDeskLevelSummaryDataSaved(true);
        } else {
          setSummaryData(SummaryDataModel.init(assessmentId));
          setIsDeskLevelSummaryDataSaved(false);
        }
      });
  };

  //get years list for national level summary result for specific assessment.
  const bindSummaryYears = () => {
    dqaService
      .getDeskLevelSummaryYears(assessmentId)
      .then((summaryYear: Array<string>) => {
        // Filter out invalid year values
        const validYears = summaryYear.filter(year => {
          const parsedYear = parseInt(year);
          return !isNaN(parsedYear) && parsedYear > 1900 && parsedYear <= 2100;
        });

        setYears(validYears);
        if (validYears.length > 0) {
          setSummaryYear(validYears[0]);
          bindNationalDataQualityEstimates(validYears[0]);
        } else {
          console.warn("No valid years found in summary data:", summaryYear);
        }
      })
      .catch((error) => {
        console.error("Failed to load summary years:", error);
      });
  };

  const GetDeskLevelSummaryReportRequest = (isFinalized: boolean) => {
    
    // summaryData.reportCompleteness = serviceLevelData?.reportCompleteness;
    // summaryData.reportTimeliness = serviceLevelData?.reportTimeliness;
    // summaryData.variableCompleteness = serviceLevelData?.variableCompleteness;
    // summaryData.variableConsistency = serviceLevelData?.variableConsistency;
    // summaryData.variableConcordance = serviceLevelData?.variableConcordance;

    if(!isConcordanceApplicable){
          summaryData.variableConcordance = null;
    }

    // Validate summaryYear before parsing
    const parsedYear = parseInt(summaryYear);
    if (isNaN(parsedYear) || parsedYear <= 0 || parsedYear > 9999) {
      throw new Error(`Invalid year value: ${summaryYear}. Please select a valid year.`);
    }

    // Create request object that matches the backend DTO structure
    const request = {
      summaryData: {
        assessmentId: assessmentId,
        reportCompleteness: summaryData?.reportCompleteness,
        reportTimeliness: summaryData?.reportTimeliness,
        variableCompleteness: summaryData?.variableCompleteness,
        variableConsistency: summaryData?.variableConsistency,
        variableConcordance: summaryData?.variableConcordance,
        malariaOutpatientProportion: summaryData?.malariaOutpatientProportion,
        malariaInPatientProportion: summaryData?.malariaInPatientProportion,
        malariaInPatientDeathProportion: summaryData?.malariaInPatientDeathProportion,
        testPositivityRate: summaryData?.testPositivityRate,
        slidePositivityRate: summaryData?.slidePositivityRate,
        rdtPositivityRate: summaryData?.rdtPositivityRate,
        suspectedTestProportion: summaryData?.suspectedTestProportion,
        // Backend expects these properties that frontend doesn't have
        keyVariableCompletenessWithinRegister: null,
        keyVariableConcordanceBtwRegister: null,
        dataSoucesError: null,
        type: DQADLSummaryResultType.NationalLevelTarget, // byte
        year: parsedYear, // short
        dataQualityResultReason: summaryData?.dataQualityResultReason,
        isFinalized: isFinalized
      }
    };
    return request;
  };

  // Triggers whenever user clicks on Save button
  const onSave = () => {
    const isFormValid = validate(summaryData);

    if (isFormValid) {
      try {
        const request = GetDeskLevelSummaryReportRequest(false);
        dqaService.saveSummaryResult(request);
        setIsDeskLevelSummaryDataSaved(true);
      } catch (error) {
        console.error("Save failed:", error);
        alert(error instanceof Error ? error.message : "Failed to save data. Please check your inputs.");
      }
    }
  };

  // Triggers whenever user clicks on finalize button
  const onFinalize = () => {
    const isFormValid = validate(summaryData);
    if (isFormValid) {
      try {
        const request = GetDeskLevelSummaryReportRequest(true);

        dqaService.finalizeSummaryResult(request).then(response => {
          if (response) {
            setSummaryData({
              ...summaryData,
              isFinalized: response as boolean,
            });
          }
        });
      } catch (error) {
        console.error("Finalize failed:", error);
        alert(error instanceof Error ? error.message : "Failed to finalize data. Please check your inputs.");
      }
    }
  };

  // Get CSS class for completeness core variables to highlight percentage value
  const getCssClassForNationalLevelResult = (value: any) => {
    if (!value && value !== 0) {
      return;
    }
    switch (true) {
      case value < 80 || value > 100:
        return "bg-red";
      case value >= 80 && value <= 95:
        return "bg-yellow";
      case value > 95 && value <= 100:
        return "bg-green";
      default:
        return "";
    }
  };

  // Triggers whenever user clicks on Generate Template button
  const onGenerateTemplate = () => {
    const downloadedTemplateName = t("app.DQADeskLevelToolReportFileName");
    setCurrentExportSteps(exportSteps);
    setActiveStepIndex(0);
    dqaService
      .downloadReport(assessmentId, handleProgress, handleAllComplete)
      .then((blob: Blob) => {
        resetExportProgress();
        UtilityHelper.download(blob, downloadedTemplateName);
      })
      .catch(() => {
        resetExportProgress();
      });
  };

  return (
    <>
      <div className={classNames(classes.indicatorWrapper)}>
        <div className='assess-header-section d-flex pt-2 pb-2'>
          <Breadcrumbs
            parentLabel={t(
              "Assessment.DataCollection.DataQualityAssessment.DataAnalysis"
            )}
            label={t(
              "Assessment.DataCollection.DataQualityAssessment.SummaryResults"
            )}
            onNavigate={onBackOrCloseButtonClick}
          />
          <div className='ml-auto'>
            <IconButton
              onClick={onBackOrCloseButtonClick}
              title='Close'
              className='close-modal'
            >
              <CancelIcon />
            </IconButton>
          </div>
        </div>

        <div className={classNames("summary-wrapper", "p-3")}>
          <div className='row justify-content-center'>
            <div className='col-sm-3'>
              <div className='d-flex justify-content-center mb-3'>
                <Dropdown
                  id='year'
                  disabled={true}
                  name='year'
                  variant='outlined'
                  size='small'
                  label={t("Common.YearOfData")}
                  value={summaryYear}
                  options={years.map(year => {
                    return new MultiSelectModel(
                      year,
                      year.toString(),
                      false,
                      false
                    );
                  })}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeYears(
                      "year",
                      "reportCompleteness",
                      "reportTimeliness",
                      "variableCompleteness",
                      "variableConsistency",
                      "variableConcordance",
                      "dataQualityResultReason",
                      e
                    )
                  }
                  error={years == null && errors["year"] && errors["year"]}
                  helperText={years == null && errors["year"] && errors["year"]}
                />
              </div>
            </div>
          </div>

          <Table>
            <>
              <TableHeader headers={tableHeadersDataQuality} />
              {serviceLevelData.reportCompleteness !== null ? (
                <TableBody>
                  <>
                    <TableRow>
                      <>
                        <TableCell>
                          <b>
                            1.2.1{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.ReportCompleteness"
                            )}
                          </b>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`read-only-textbox  ${getCssClassForNationalLevelResult(serviceLevelData?.reportCompleteness)}`}
                          >
                            {serviceLevelData?.reportCompleteness}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TextBox
                            type='number'
                            id='reportCompleteness'
                            name='reportCompleteness'
                            maxLength={3}
                            inputProps={{
                              max: 100,
                              min: 0,
                            }}
                            className={`col-form-control inputfocus`}
                            value={summaryData?.reportCompleteness}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetHandleChange(
                                "reportCompleteness",
                                e
                              );
                            }}
                            error={
                              summaryData?.reportCompleteness == null &&
                              errors["reportCompleteness"] &&
                              errors["reportCompleteness"]
                            }
                            helperText={
                              summaryData?.reportCompleteness == null &&
                              errors["reportCompleteness"] &&
                              errors["reportCompleteness"]
                            }
                          />
                        </TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <b>
                            1.2.3{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.ReportTimeliness"
                            )}
                          </b>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`read-only-textbox  ${getCssClassForNationalLevelResult(serviceLevelData?.reportTimeliness)}`}
                          >
                            {serviceLevelData?.reportTimeliness}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TextBox
                            type='number'
                            id='reportTimeliness'
                            name='reportTimeliness'
                            maxLength={3}
                            inputProps={{
                              max: 100,
                              min: 0,
                            }}
                            className={`col-form-control inputfocus`}
                            value={summaryData?.reportTimeliness}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetHandleChange(
                                "reportTimeliness",
                                e
                              );
                            }}
                            error={
                              summaryData?.reportTimeliness == null &&
                              errors["reportTimeliness"] &&
                              errors["reportTimeliness"]
                            }
                            helperText={
                              summaryData?.reportTimeliness == null &&
                              errors["reportTimeliness"] &&
                              errors["reportTimeliness"]
                            }
                          />
                        </TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <b>
                            1.2.7{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.VariableCompleteness"
                            )}
                          </b>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`read-only-textbox  ${getCssClassForNationalLevelResult(serviceLevelData?.variableCompleteness)}`}
                          >
                            {serviceLevelData?.variableCompleteness}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TextBox
                            type='number'
                            id='variableCompleteness'
                            name='variableCompleteness'
                            maxLength={3}
                            inputProps={{
                              max: 100,
                              min: 0,
                            }}
                            className={`col-form-control inputfocus`}
                            value={summaryData?.variableCompleteness}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetHandleChange(
                                "variableCompleteness",
                                e
                              );
                            }}
                            error={
                              summaryData?.variableCompleteness == null &&
                              errors["variableCompleteness"] &&
                              errors["variableCompleteness"]
                            }
                            helperText={
                              summaryData?.variableCompleteness == null &&
                              errors["variableCompleteness"] &&
                              errors["variableCompleteness"]
                            }
                          />
                        </TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <b>
                            1.2.8{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.VariableConsistency"
                            )}
                          </b>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`read-only-textbox  ${getCssClassForNationalLevelResult(serviceLevelData?.variableConsistency)}`}
                          >
                            {serviceLevelData?.variableConsistency}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TextBox
                            type='number'
                            id='variableConsistency'
                            name='variableConsistency'
                            maxLength={3}
                            inputProps={{
                              max: 100,
                              min: 0,
                            }}
                            className={`col-form-control inputfocus`}
                            value={summaryData?.variableConsistency}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetHandleChange(
                                "variableConsistency",
                                e
                              );
                            }}
                            error={
                              summaryData?.variableConsistency == null &&
                              errors["variableConsistency"] &&
                              errors["variableConsistency"]
                            }
                            helperText={
                              summaryData?.variableConsistency == null &&
                              errors["variableConsistency"] &&
                              errors["variableConsistency"]
                            }
                          />
                        </TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <b>
                            1.2.10{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.VariableConcordance"
                            )}
                          </b>
                        </TableCell>

                        <TableCell>
                          {isConcordanceApplicable === null ? null : isConcordanceApplicable ? (
                            <div
                              className={`read-only-textbox  ${getCssClassForNationalLevelResult(serviceLevelData?.variableConcordance)}`}
                            >
                              {serviceLevelData?.variableConcordance}
                            </div>
                          ) : (
                            <span>{t("Common.NotAssessed")}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isConcordanceApplicable === true && <TextBox
                            type='number'
                            id='variableConcordance'
                            name='variableConcordance'
                            maxLength={3}
                            inputProps={{
                              max: 100,
                              min: 0,
                            }}
                            className={`col-form-control inputfocus`}
                            value={summaryData?.variableConcordance}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetHandleChange(
                                "variableConcordance",
                                e
                              );
                            }}
                            error={
                              summaryData?.variableConcordance == null &&
                              errors["variableConcordance"] &&
                              errors["variableConcordance"]
                            }
                            helperText={
                              summaryData?.variableConcordance == null &&
                              errors["variableConcordance"] &&
                              errors["variableConcordance"]
                            }
                          />}
                        </TableCell>
                      </>
                    </TableRow>

                    <TableRow>
                      <>
                        <TableCell>
                          <b>
                            1.2.9{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.ConsitentOverTime"
                            )}
                          </b>
                          <IconButton className='grid-icon-button'>
                            <Tooltip
                              content={t(
                                "Assessment.DataAnalysis.DQASummaryTooltip"
                              )}
                              isHtml
                            >
                              <InfoIcon fontSize='small' />
                            </Tooltip>
                          </IconButton>
                          {
                            //Show error message if Consistency Overtime Core Indicators aren't selected
                            (errors["malariaOutpatientProportion"] ||
                              errors["malariaInPatientProportion"] ||
                              errors["malariaInPatientDeathProportion"] ||
                              errors["testPositivityRate"] ||
                              errors["slidePositivityRate"] ||
                              errors["rdtPositivityRate"] ||
                              errors["suspectedTestProportion"]) && (
                              <span className='Mui-error d-flex mb-2'>
                                *
                                {t(
                                  "Assessment.DataCollection.DataQualityAssessment.ConsitentOverTimeResponseError"
                                )}
                              </span>
                            )
                          }
                        </TableCell>

                        <TableCell>
                          <div className='disableContent'>
                            <RadioButtonGroup
                              id='consistencyOverTimeCoreIndicators'
                              name='consistencyOverTimeCoreIndicators'
                              color='primary'
                              key={`consistency-${summaryData.consistencyOverTimeCoreIndicators}`}
                              value={
                                summaryData.consistencyOverTimeCoreIndicators
                              }
                              options={[
                                new MultiSelectModel(
                                  true,
                                  t("indicators-responses:Common:Met")
                                ),
                                new MultiSelectModel(
                                  false,
                                  t("indicators-responses:Common:NotMet")
                                ),
                              ]}
                            />
                          </div>
                        </TableCell>
                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow className='border'>
                      <>
                        <TableCell>
                          {t("Assessment.DataAnalysis.DQASummaryInfoText")}
                        </TableCell>
                        <TableCell>
                          <b>
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.ConsistentTrend"
                            )}
                          </b>
                        </TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            1.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.MalariaOutpatients"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='malariaOutpatientProportion'
                            name='malariaOutpatientProportion'
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.malariaOutpatientProportion
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.malariaOutpatientProportion,
                              "malariaOutpatientProportion"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "malariaOutpatientProportion",
                                e
                              );
                            }}
                            error={
                              summaryData?.malariaOutpatientProportion ==
                                null &&
                              errors["malariaOutpatientProportion"] &&
                              errors["malariaOutpatientProportion"]
                            }
                            helperText={
                              summaryData?.malariaOutpatientProportion ==
                                null &&
                              errors["malariaOutpatientProportion"] &&
                              errors["malariaOutpatientProportion"]
                            }
                          />
                        </TableCell>

                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            2.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.MalariaInpatients"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='malariaInPatientProportion'
                            name='malariaInPatientProportion'
                            row
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.malariaInPatientProportion
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.malariaInPatientProportion,
                              "malariaInPatientProportion"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "malariaInPatientProportion",
                                e
                              );
                            }}
                            error={
                              summaryData?.malariaInPatientProportion == null &&
                              errors["malariaInPatientProportion"] &&
                              errors["malariaInPatientProportion"]
                            }
                            helperText={
                              summaryData?.malariaInPatientProportion == null &&
                              errors["malariaInPatientProportion"] &&
                              errors["malariaInPatientProportion"]
                            }
                          />
                        </TableCell>

                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            3.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.MalariaInpatientsDeath"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='malariaInPatientDeathProportion'
                            name='malariaInPatientDeathProportion'
                            row
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.malariaInPatientDeathProportion
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.malariaInPatientDeathProportion,
                              "malariaInPatientDeathProportion"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "malariaInPatientDeathProportion",
                                e
                              );
                            }}
                            error={
                              summaryData?.malariaInPatientDeathProportion ==
                                null &&
                              errors["malariaInPatientDeathProportion"] &&
                              errors["malariaInPatientDeathProportion"]
                            }
                            helperText={
                              summaryData?.malariaInPatientDeathProportion ==
                                null &&
                              errors["malariaInPatientDeathProportion"] &&
                              errors["malariaInPatientDeathProportion"]
                            }
                          />
                        </TableCell>

                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            4.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.TestPositivityRate"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='testPositivityRate'
                            name='testPositivityRate'
                            row
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.testPositivityRate
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.testPositivityRate,
                              "testPositivityRate"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "testPositivityRate",
                                e
                              );
                            }}
                            error={
                              summaryData?.testPositivityRate == null &&
                              errors["testPositivityRate"] &&
                              errors["testPositivityRate"]
                            }
                            helperText={
                              summaryData?.testPositivityRate == null &&
                              errors["testPositivityRate"] &&
                              errors["testPositivityRate"]
                            }
                          />
                        </TableCell>
                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            5.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.SidePositivityRate"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='slidePositivityRate'
                            name='slidePositivityRate'
                            row
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.slidePositivityRate
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.slidePositivityRate,
                              "slidePositivityRate"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "slidePositivityRate",
                                e
                              );
                            }}
                            error={
                              summaryData?.slidePositivityRate == null &&
                              errors["slidePositivityRate"] &&
                              errors["slidePositivityRate"]
                            }
                            helperText={
                              summaryData?.slidePositivityRate == null &&
                              errors["slidePositivityRate"] &&
                              errors["slidePositivityRate"]
                            }
                          />
                        </TableCell>

                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            6.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.RDTPositivityRate"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='rdtPositivityRate'
                            name='rdtPositivityRate'
                            row
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.rdtPositivityRate
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.rdtPositivityRate,
                              "rdtPositivityRate"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "rdtPositivityRate",
                                e
                              );
                            }}
                            error={
                              summaryData?.rdtPositivityRate == null &&
                              errors["rdtPositivityRate"] &&
                              errors["rdtPositivityRate"]
                            }
                            helperText={
                              summaryData?.rdtPositivityRate == null &&
                              errors["rdtPositivityRate"] &&
                              errors["rdtPositivityRate"]
                            }
                          />
                        </TableCell>

                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                    <TableRow>
                      <>
                        <TableCell>
                          <i>
                            7.{" "}
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.suspectedTests"
                            )}
                          </i>
                        </TableCell>

                        <TableCell>
                          <RadioButtonGroup
                            id='suspectedTestProportion'
                            name='suspectedTestProportion'
                            row
                            color='primary'
                            options={[
                              new MultiSelectModel(
                                ConsistentTrendValue.Yes,
                                t("indicators-responses:Common:Yes")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.No,
                                t("indicators-responses:Common:No")
                              ),
                              new MultiSelectModel(
                                ConsistentTrendValue.NA,
                                t("indicators-responses:Common:NA")
                              ),
                            ]}
                            value={ConsistentTrendHelper.fromBoolean(
                              summaryData?.suspectedTestProportion
                            )}
                            defaultValue={bindConsistencyOverTimeForCoreIndicators(
                              summaryData?.suspectedTestProportion,
                              "suspectedTestProportion"
                            )}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              nationalLevelTargetRadioChange(
                                "suspectedTestProportion",
                                e
                              );
                            }}
                            error={
                              summaryData?.suspectedTestProportion == null &&
                              errors["suspectedTestProportion"] &&
                              errors["suspectedTestProportion"]
                            }
                            helperText={
                              summaryData?.suspectedTestProportion == null &&
                              errors["suspectedTestProportion"] &&
                              errors["suspectedTestProportion"]
                            }
                          />
                        </TableCell>

                        <TableCell>{}</TableCell>
                      </>
                    </TableRow>
                  </>
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <>
                      <TableCell></TableCell>
                      <TableCell>
                        <>
                          <div className='d-flex h-200 p-3 align-items-center'>
                            {t(
                              "Assessment.DataCollection.DataQualityAssessment.NoRecordsAvailable"
                            )}
                          </div>
                        </>
                      </TableCell>
                      <TableCell></TableCell>
                    </>
                  </TableRow>
                </TableBody>
              )}
            </>
          </Table>

          {serviceLevelData.reportCompleteness !== null ? (
            <div className='col-xs-12 col-md-6'>
              <div className='mt-3'>
                <p className='fw-bold'>
                  <span>
                    1.2.14 &nbsp;
                    {t(
                      "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.dataQualityReason"
                    )}
                  </span>
                </p>
                <TextBox
                  id='dataQualityResultReason'
                  name='dataQualityResultReason'
                  placeholder={t(
                    "Assessment.DataCollection.DataQualityAssessment.nationalLevelSummary.dataQualityReasonText"
                  )}
                  rows={6}
                  multiline
                  fullWidth
                  value={summaryData?.dataQualityResultReason}
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                    dataQualityResultReasonChange(
                      "dataQualityResultReason",
                      evt
                    );
                  }}
                  onPaste={onDataQualityReasonPaste}
                  maxLength={MAX_DATA_QUALITY_REASON_LENGTH}
                  error={
                    (errors["dataQualityResultReason"] ||
                      dataQualityReasonError) &&
                    true
                  }
                  helperText={
                    dataQualityReasonError ||
                    (errors["dataQualityResultReason"] &&
                      errors["dataQualityResultReason"])
                  }
                />
              </div>
            </div>
          ) : (
            <></>
          )}
          {serviceLevelData.reportCompleteness !== null ? (
            <div className='col-xs-12 col-md-12 mt-3'>
              <p className='mb-0'>
                {parse(
                  t(
                    "Assessment.DataCollection.DataQualityAssessment.LegendTextCoreVariables"
                  )
                )}
              </p>
            </div>
          ) : (
            <></>
          )}

          <div className='response-wrapper'>
            <div className='button-action-section d-flex justify-content-center py-3'>
              {canEditAndSaveDeskLevelDQASummaryResult &&
                serviceLevelData.reportCompleteness !== null &&
                !summaryData?.isFinalized && (
                  <Button
                    className={classNames("btn", "app-btn-secondary")}
                    onClick={onSave}
                    disabled={Boolean(dataQualityReasonError)}
                  >
                    {t("Common.Save")}
                  </Button>
                )}
              {canFinalizeDeskLevelDQASummaryResult &&
                isDeskLevelSummaryDataSaved && (
                  <Button
                    className={classNames("btn", "app-btn-secondary")}
                    onClick={onFinalize}
                    disabled={Boolean(dataQualityReasonError)}
                  >
                    {t("Common.Finalize")}
                  </Button>
                )}

              {summaryData?.isFinalized && canExportDeskLevelDQASummary && (
                <Button
                  className={classNames("btn", "app-btn-secondary")}
                  onClick={onGenerateTemplate}
                  disabled={Boolean(dataQualityReasonError)}
                >
                  {t("Common.Export")}
                </Button>
              )}

            </div>
          </div>
        </div>
      </div>

      <Dialog open={activeStepIndex >= 0} disableEscapeKeyDown>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography>{currentExportSteps[0] ?? `${t("Common.Exporting")}...`}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStepIndex} alternativeLabel sx={{ minWidth: 480 }}>
            {currentExportSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeskLevelSummaryComponent;
